import { useCallback, useEffect, useRef } from "react";

function getChars(value) {
  return String(value || "").padEnd(6, " ").slice(0, 6).split("");
}

export default function OtpInput({ value = "", onChange, disabled = false, autoFocus = false }) {
  const refs = useRef([]);

  const focusIndex = useCallback((index) => {
    const el = refs.current[index];
    if (el) {
      el.focus();
      el.select();
    }
  }, []);

  useEffect(() => {
    if (autoFocus) focusIndex(0);
  }, [autoFocus, focusIndex]);

  const applyValue = useCallback(
    (chars) => {
      onChange(chars.join("").replace(/\s/g, "").slice(0, 6));
    },
    [onChange]
  );

  const handleChange = (index, raw) => {
    const cleaned = raw.replace(/\D/g, "");
    const chars = getChars(value);
    if (!cleaned) {
      chars[index] = "";
      applyValue(chars);
      return;
    }
    if (cleaned.length > 1) {
      const pasted = cleaned.slice(0, 6).split("");
      while (pasted.length < 6) pasted.push("");
      applyValue(pasted);
      focusIndex(Math.min(pasted.filter(Boolean).length, 5));
      return;
    }
    chars[index] = cleaned[0];
    applyValue(chars);
    if (index < 5) focusIndex(index + 1);
  };

  const handleKeyDown = (index, e) => {
    const chars = getChars(value);
    if (e.key === "Backspace") {
      if (chars[index] && chars[index] !== " ") {
        chars[index] = "";
        applyValue(chars);
        return;
      }
      if (index > 0) {
        e.preventDefault();
        chars[index - 1] = "";
        applyValue(chars);
        focusIndex(index - 1);
      }
    }
    if (e.key === "ArrowLeft" && index > 0) focusIndex(index - 1);
    if (e.key === "ArrowRight" && index < 5) focusIndex(index + 1);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    const chars = pasted.split("");
    while (chars.length < 6) chars.push("");
    applyValue(chars);
    focusIndex(Math.min(pasted.length, 5));
  };

  const digits = getChars(value);

  return (
    <div className="otp-boxes" onPaste={handlePaste}>
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            refs.current[index] = el;
          }}
          className="otp-box"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          value={digit === " " ? "" : digit}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onFocus={(e) => e.target.select()}
          disabled={disabled}
          aria-label={`Digit ${index + 1}`}
        />
      ))}
    </div>
  );
}
